import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';

const Section = ({ title, children }) => (
  <section className="scroll-mt-32">
    <h2 className="font-display text-2xl lg:text-3xl text-ink-900">{title}</h2>
    <div className="mt-3 space-y-3 text-ink-700 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPage = () => {
  usePageTitle(`Privacy Policy | ${BRAND_INFO.name}`,
    `${BRAND_INFO.name} Privacy Policy — what data we collect, how we store it, your rights, and how we handle your bloodwork results.`);

  return (
    <>
      <Header />
      <main className="pt-28 lg:pt-36 pb-24 bg-cream-100">
        <div className="container-page">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-4 font-display text-display-lg text-ink-900">Privacy Policy</h1>
          <p className="mt-4 text-ink-600">Last updated: July 2025</p>

          <p className="mt-8 text-ink-700 leading-relaxed">
            {CONTACT_INFO.corporateName} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;) operates {BRAND_INFO.name} at {BRAND_INFO.domain}. This Privacy Policy explains what information we collect, how we use it, with whom we share it, and the choices you have. We treat your bloodwork results with the seriousness they deserve. We took the time to write this in plain language.
          </p>

          <div className="mt-12 space-y-10">
            <Section title="1. Information We Collect">
              <p><strong>Account information.</strong> When you create an account we collect your full legal name, date of birth, biological sex, email address, and a hashed password. Name and date of birth are required because every lab requisition must legally identify you.</p>
              <p><strong>Order information.</strong> The panels and individual biomarkers you select, the partner draw location you choose, and the lab requisition itself.</p>
              <p><strong>Lab results.</strong> The blood-test results returned to us by our partner labs (LabCorp, Quest Diagnostics, BioReference) for orders you placed through {BRAND_INFO.name}, plus any results you upload yourself by PDF.</p>
              <p><strong>Billing information.</strong> Our payment processor collects your billing name and address. We do not store your full payment card number on our servers.</p>
              <p><strong>Usage information.</strong> Standard server logs (IP address, browser, device type, pages viewed, errors). We use this for security and reliability.</p>
            </Section>

            <Section title="2. How We Use Information">
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Generate and route physician-signed lab requisitions to partner labs.</li>
                <li>Display, plot, and unify your results in your dashboard.</li>
                <li>Process payments and provide receipts.</li>
                <li>Respond to support inquiries and refund requests.</li>
                <li>Send transactional emails (order confirmations, result-ready notifications, billing, security).</li>
                <li>Improve reliability, debug issues, and prevent abuse.</li>
                <li>Comply with legal and regulatory obligations.</li>
              </ul>
              <p>We do <strong>not</strong> sell your personal information. We do <strong>not</strong> serve targeted advertising based on your bloodwork results.</p>
            </Section>

            <Section title="3. Sharing">
              <p>We share information only with: (a) partner clinical laboratories and the licensed independent physicians who sign your requisitions, strictly for the purpose of fulfilling your order; (b) service providers (payment processors, hosting, email delivery) under written contracts that limit their use of your data; (c) law enforcement or other parties when required by valid legal process; or (d) a successor in the event of a merger, acquisition, or sale of substantially all of our assets, in which case the successor will be bound by this Policy.</p>
            </Section>

            <Section title="4. Security">
              <p>We protect data in transit with TLS 1.2+ and apply industry-standard safeguards to data at rest. Access to lab results is restricted to your authenticated session. No system can be guaranteed perfectly secure, but we work to follow current best practices and to disclose breaches as required by law.</p>
            </Section>

            <Section title="5. Cookies & Tracking">
              <p>We use a small number of strictly-necessary cookies for login, security, and shopping cart state. We use lightweight analytics that do not include your name or lab values. We do not run third-party advertising trackers on pages where you can view your dashboard.</p>
            </Section>

            <Section title="6. Your Rights">
              <p>Depending on where you live, you may have the right to (a) access the personal information we hold about you, (b) correct inaccuracies, (c) request deletion, (d) opt out of certain uses, and (e) lodge a complaint with a regulator. To exercise any of these rights, email {CONTACT_INFO.email} from the address associated with your account.</p>
              <p>California residents: under the California Consumer Privacy Act (CCPA/CPRA), you have specific rights to know, correct, delete, and opt out of &ldquo;sales&rdquo; or &ldquo;sharing&rdquo; of personal information. We do not sell or share personal information for cross-context behavioral advertising.</p>
            </Section>

            <Section title="7. Data Retention">
              <p>We retain account information and lab results for as long as your account is active so that your timeline remains intact across draws. After account closure we retain a record of completed orders and resulting lab values for the period required by federal and state record-keeping laws applicable to clinical lab fulfilment, after which the records are securely deleted or anonymized.</p>
            </Section>

            <Section title="8. Children">
              <p>The Service is not directed to children under 18. We do not knowingly collect personal information from children. If you believe a child has provided us information, please email {CONTACT_INFO.email} and we will delete it.</p>
            </Section>

            <Section title="9. International Users">
              <p>The Service is operated from the United States and currently fulfils draws inside the United States only. If you access the website from outside the U.S., your information may be processed in the United States, where data-protection laws may differ from those in your country.</p>
            </Section>

            <Section title="10. Changes to This Policy">
              <p>We may update this Policy from time to time. The &ldquo;Last updated&rdquo; date will reflect the latest revision. For material changes we will provide additional notice by email or in-app prompt where reasonable.</p>
            </Section>

            <Section title="11. Contact">
              <p>For privacy questions or to exercise your rights, contact us at:</p>
              <ul className="list-disc pl-5">
                <li>{CONTACT_INFO.corporateName}</li>
                <li>{CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</li>
                <li>Email: {CONTACT_INFO.email}</li>
                <li>Phone: {CONTACT_INFO.phone}</li>
              </ul>
              <p>See also our <Link to="/terms" className="underline text-brick-500">Terms &amp; Conditions</Link>.</p>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPage;
